import { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  DialogProps,
  FormControlLabel,
  Grid,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { useModal } from "mui-modal-provider";

//Components
import BasicModal from "../../components/modal/BasicModal";
import TableSkeleton from "../../components/skeleton/TableSkeleton";
import MarkWorkFlowConfirmationModal from "./MarkWorkFlowConfirmationModal";

//component prop types
interface Props extends DialogProps {
  onClose: () => void;
  progress?: any;
  isLoadingProgress?: any;
  progressHistory?: any;
  isLoadingProgressHistory?: any;
  refetchProgressHistory: () => void;
  caseId?: any;
}

/**
 * MarkWorkFlowAsCompletedModal component for complete workflow multiple to-dos in one time and no email notification will be sent.
 * @component MarkWorkFlowAsCompletedModal
 * @author Sanjay
 *
 */

const MarkWorkFlowAsCompletedModal = ({
  onClose,
  progress,
  isLoadingProgress,
  progressHistory,
  isLoadingProgressHistory,
  refetchProgressHistory,
  caseId,
  ...props
}: Props) => {
  const { showModal } = useModal();

  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>(
    progressHistory?.filter((task: any) => task?.isCompleted) || []
  );

  const isSelectedTask = useMemo(
    () => selectedTaskIds?.some((t: any) => !t?.isCompleted) ?? false,
    [selectedTaskIds]
  );

  const handleToggleTask = (task: any) => {
    setSelectedTaskIds((prev: any[]) => {
      const exists = prev.some((t) => t._id === task._id);
      if (exists) {
        return prev.filter((t) => t._id !== task._id); // Remove
      } else {
        return [...prev, task]; // Add
      }
    });
  };

  return (
    <BasicModal
      onClose={onClose}
      title="Select the tasks you want to complete"
      height="calc(100vh - 230px)"
      size="sm"
      content={
        <Grid container spacing={3}>
          {isLoadingProgressHistory || isLoadingProgress ? (
            <TableSkeleton />
          ) : (
            <>
              <Typography variant="h6" gutterBottom p={2} mt={1} ml={1} mb={0}>
                {progress}
              </Typography>
              {progressHistory?.map((workflow: any, i: any) => (
                <Grid item xs={12} key={i} mt={-2}>
                  <Box pl={2}>
                    <Box key={workflow._id} sx={{ display: "block" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedTaskIds?.some(
                              (t: any) => t?._id === workflow._id
                            )}
                            disabled={workflow?.isCompleted}
                            onChange={() => handleToggleTask(workflow)}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "normal" }}
                          >
                            {workflow?.task}
                          </Typography>
                        }
                      />
                    </Box>
                  </Box>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Alert
                  sx={{
                    height: 35,
                    pt: 0,
                    pl: 0,
                    pb: 0,
                    mb: -2,
                    overflow: "hidden",
                    lineHeight: 1,
                    alignItems: "center",
                    backgroundColor: "transparent", // Remove background color
                    boxShadow: "none", // Remove box shadow
                    border: "none", // Remove border
                    color: "#d32f2f",
                    fontSize: "14px",
                  }}
                  severity="error"
                >
                  No email notification will be sent when completing tasks from
                  this section.
                </Alert>
              </Grid>
            </>
          )}
        </Grid>
      }
      actions={
        <Grid item xs={12} textAlign="right">
          <Button
            color="error"
            variant="outlined"
            sx={{ mr: 4, textTransform: "capitalize" }}
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={selectedTaskIds.length === 0 || !isSelectedTask}
            sx={{ textTransform: "capitalize" }}
            onClick={() => {
              const modal: any = showModal(MarkWorkFlowConfirmationModal, {
                onClose: () => modal.hide(),
                onCloseMainModal: () => {
                  onClose();
                },
                selectedTaskIds,
                caseId,
                refetchProgressHistory,
              });
            }}
          >
            Update
          </Button>
        </Grid>
      }
      {...props}
    />
  );
};

export default MarkWorkFlowAsCompletedModal;
